import { ApolloError } from "@apollo/client";
import { Spinner } from "@heroui/react";
import { InputAdornment } from "@mui/material";
import Popover from "@mui/material/Popover";
import { useGetStationsForAccountRole } from "apollo/hooks/queries";
import { useNavbarHeightContext } from "context/NavbarHeightContext";
import { useSenderAccountContext } from "context/SenderAccountContext";
import {
  ACCOUNT_ROLE_PREDICT,
  PRODUCT_FEATURES,
  QUERY_PROGRESS_PENDING,
} from "core/consts";
import { getProductFeatureActive } from "core/model/productfeatures";
import {
  CheckBoxGroupOption,
  FilterCheckboxGroup,
} from "ds/ui/filter-checkbox-group/filter-checkbox-group";
import RSButton from "ds_legacy/components/RSButton";
import { StyledPaper } from "ds_legacy/components/Tables/GeneralTable/styles";
import { TextInputField } from "ds_legacy/components/TextInputField";
import { dp, margin } from "ds_legacy/materials/metrics";
import { Body } from "ds_legacy/materials/typography";
import { useHL7ImportStatusContext } from "dsl/atoms/HL7ImportStatusContext";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { StationCheckboxGroup } from "dsl/organisms/Accounts/StationCheckboxGroup";
import { BookmarkIcon, PencilIcon, SearchIcon } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslations } from "translations";
import RestrictedAccess from "../RestrictedAccessPage";
import { usePredictData } from "./hooks/usePredictData";
import { PredictStation, usePredictFilters } from "./hooks/usePredictFilters";
import PatientsTable from "./PatientsTable";
import PredictDashboardEmptyState from "./PredictDashboardEmptyState";

export const FILTER_TYPES = {
  ALL: 1,
  HIGH_PROBABILITY: 2,
  MEDIUM_PROBABILITY: 3,
  LOW_PROBABILITY: 4,
  BOOKMARKED: 5,
} as const;

export const PROBABILITY_THRESHOLDS = {
  HIGH: 81,
  MEDIUM: 51,
} as const;

export const MAX_STATION_CHIPS = 4;

type StationSelectorPopoverProps = {
  allStationsFull: PredictStation[];
  applyStationSelections: () => void;
  handleStationToggle: (stationId: number, checked: boolean) => void;
  resetTempSelections: () => void;
  selectedStations: PredictStation[];
  stationsError: ApolloError | undefined;
  stationsLoading: string;
  tempSelectedStationIds: number[];
};

function StationSelectorPopover({
  allStationsFull,
  applyStationSelections,
  handleStationToggle,
  resetTempSelections,
  selectedStations,
  stationsError,
  stationsLoading,
  tempSelectedStationIds,
}: StationSelectorPopoverProps) {
  const translations = useTranslations();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const stationButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <RSButton
        id="station-selector-button"
        loading="na"
        color="primary"
        size="small"
        variant="outlined"
        style={{
          marginLeft: dp(0),
          borderRadius: dp(20),
          padding: `${dp(4)} ${dp(12)}`,
        }}
        onClick={() => setAnchorEl(stationButtonRef.current)}
        ref={stationButtonRef}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        aria-controls="station-selection-menu"
        ariaProps={{
          "aria-haspopup": "true",
          "aria-expanded": `${Boolean(anchorEl)}`,
          "aria-controls": "station-selection-menu",
        }}
        aria-label={`${translations.predict.dashboard.filterChipStations} (${tempSelectedStationIds.length})`}
        LeftIcon={PencilIcon}
      >
        {translations.predict.dashboard.filterChipStations} (
        {tempSelectedStationIds.length})
      </RSButton>

      <Popover
        id="station-selection-menu"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        style={{ marginTop: dp(4) }}
        aria-label={
          translations.predict.dashboard.ariaLabelStationsFilterPopover
        }
      >
        {stationsLoading === QUERY_PROGRESS_PENDING && (
          <Spinner id="stations-spinner" size="sm" />
        )}

        {stationsError && <Body>{translations.general.error}</Body>}
        <div style={{ width: 300, padding: 16 }}>
          {allStationsFull.length > 0 && (
            <StationCheckboxGroup
              isPredictDashboard
              stations={selectedStations.map((station) => ({
                label: station.name,
                value: station.id,
              }))}
              selectedStations={tempSelectedStationIds}
              onStationToggle={handleStationToggle}
            />
          )}
          <div className="mt-4 flex justify-end">
            <RSButton
              id="close"
              color="primary"
              size="small"
              variant="outlined"
              onClick={() => {
                resetTempSelections();
                setAnchorEl(null);
              }}
              loading="na"
              style={{ marginRight: "8px" }}
            >
              {translations.actions.cancel}
            </RSButton>
            <RSButton
              id="apply"
              color="primary"
              size="small"
              variant="contained"
              onClick={() => {
                applyStationSelections();
                setAnchorEl(null);
              }}
              loading="na"
            >
              {translations.actions.apply}
            </RSButton>
          </div>
        </div>
      </Popover>
    </>
  );
}

export default function PredictPage() {
  const { height } = useNavbarHeightContext();
  const translations = useTranslations();
  const { isTablet } = useMedia();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { hasOneWaySync, isHISActivated } = useHL7ImportStatusContext();
  const { account, careseeker } = useSenderAccountContext();
  const featureActivated = getProductFeatureActive(
    careseeker?.product_features,
    PRODUCT_FEATURES.PREDICT,
  );

  const [stations, stationsLoading, { error: stationsError }] =
    useGetStationsForAccountRole({
      account_id: account?.id,
      careseeker_id: careseeker?.id,
      role_value: ACCOUNT_ROLE_PREDICT,
    });
  const allStationsFull = stations.all_stations || [];
  const selectedStations: PredictStation[] = stations.selected_stations || [];

  const {
    activeFilters,
    applyStationSelections,
    combinedActiveFilters,
    handleFilterChange,
    handleStationToggle,
    initialStationsString,
    resetTempSelections,
    selectedStationIds,
    tempSelectedStationIds,
  } = usePredictFilters({ selectedStations });

  const { isLoading, patients } = usePredictData({
    station: initialStationsString,
  });

  const filterOptions = [
    {
      name: translations.predict.dashboard.filterChipAll,
      value: FILTER_TYPES.ALL,
    },
    {
      name: translations.predict.dashboard.filterChipHighProbability,
      value: FILTER_TYPES.HIGH_PROBABILITY,
    },
    {
      name: translations.predict.dashboard.filterChipMediumProbability,
      value: FILTER_TYPES.MEDIUM_PROBABILITY,
    },
    {
      name: translations.predict.dashboard.filterChipLowProbability,
      value: FILTER_TYPES.LOW_PROBABILITY,
    },
    {
      avatar: <BookmarkIcon size={16} />,
      name: translations.predict.dashboard.filterChipBookmark,
      value: FILTER_TYPES.BOOKMARKED,
    },
    ...(selectedStations.length <= MAX_STATION_CHIPS
      ? selectedStations.map((station) => ({
          name: station.name,
          value: station.id,
        }))
      : []),
  ];

  const checkboxOptions: CheckBoxGroupOption[] = useMemo(() => {
    return filterOptions.map((opt) => ({
      ...opt,
      label: opt.name,
      value: String(opt.value),
    }));
  }, [filterOptions]);

  if (!featureActivated) {
    return <RestrictedAccess />;
  }

  const handleCheckboxGroupChange = (val: string[]) => {
    handleFilterChange(val.map(Number));
  };

  return (
    <div className="min-h-screen w-full bg-gray-100">
      <div style={{ marginTop: dp(height) }} />
      <div className="max-w-screen-xl mx-auto w-full p-4">
        <StyledPaper
          $isTablet={isTablet}
          elevation={isTablet ? 0 : undefined}
          square={isTablet}
          style={{ width: "100%", margin: margin(0) }}
        >
          <div className="flex items-end justify-end">
            <div className="w-3/5">
              <h1 className="mb-1 text-xl font-bold">
                {translations.predict.dashboard.title}
              </h1>
              <p className="text-sm text-gray-700">
                {translations.predict.dashboard.subtitle}
              </p>
            </div>

            <div className="ml-auto w-1/3 max-w-sm">
              <TextInputField
                elementName="patient-search"
                label={translations.predict.dashboard.searchFieldPlaceholder}
                value={searchQuery}
                onChange={(value: string) => setSearchQuery(value)}
                variant="outlined"
                width="100%"
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon className="h-5 w-5 " />
                  </InputAdornment>
                }
              />
            </div>
          </div>

          <div
            className="flex items-center justify-between"
            style={{ padding: 0 }}
          >
            <div className="flex items-center">
              <div className="flex flex-wrap items-center gap-2">
                <FilterCheckboxGroup
                  aria-label="filter-options"
                  options={checkboxOptions}
                  value={combinedActiveFilters.map(String)}
                  onValueChange={handleCheckboxGroupChange}
                  orientation="horizontal"
                />
                {selectedStations.length > MAX_STATION_CHIPS && (
                  <StationSelectorPopover
                    allStationsFull={allStationsFull}
                    applyStationSelections={applyStationSelections}
                    handleStationToggle={handleStationToggle}
                    resetTempSelections={resetTempSelections}
                    selectedStations={selectedStations}
                    stationsError={stationsError}
                    stationsLoading={stationsLoading}
                    tempSelectedStationIds={tempSelectedStationIds}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="w-full overflow-x-auto">
            {isHISActivated && hasOneWaySync ? (
              <PatientsTable
                patients={patients}
                searchQuery={searchQuery}
                activeFilters={activeFilters}
                selectedStations={selectedStations}
                selectedStationIds={selectedStationIds}
                isLoading={isLoading}
                accountId={account?.id}
              />
            ) : (
              <PredictDashboardEmptyState
                imagePath="empty-state-placeholder.svg"
                imageWidth="120px"
                imageAlt="Empty state"
                message={translations.predict.dashboard.emptyStateHIS}
              />
            )}
          </div>
        </StyledPaper>
      </div>
    </div>
  );
}
